import React, { createContext, useContext, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { usePreferredColorScheme } from './utils/usePreferredColorScheme'
import { useLocalStorage } from '../../hooks'
import { THEME } from './config/theme'
import { FLAGS } from '../../config'
import { useFlagsContext } from '../RootstechFlagsProvider'

const ThemeContext = createContext()
export const useThemeContext = () => useContext(ThemeContext)

// Toggle between light and dark themes
function getNextTheme(currentTheme) {
  return currentTheme === THEME.LIGHT ? THEME.DARK : THEME.LIGHT
}

// Apply feature flag override to theme
function getEffectiveTheme(theme, isDayThemeEnabled) {
  return isDayThemeEnabled ? theme : THEME.DARK
}

export const ThemeProvider = ({ children }) => {
  const [storedValue, setStoredValue] = useLocalStorage('rootstech.theme', '')
  const [theme, setTheme] = usePreferredColorScheme(storedValue)
  const { [FLAGS.DAY_THEME]: dayThemeFlag } = useFlagsContext()

  const toggleTheme = useCallback(() => {
    setTheme((prevTheme) => {
      const newTheme = getNextTheme(prevTheme)
      setStoredValue(newTheme)
      return newTheme
    })
  }, [setTheme, setStoredValue])

  const effectiveTheme = getEffectiveTheme(theme, dayThemeFlag?.isOn)

  const value = useMemo(
    () => ({
      theme: effectiveTheme,
      toggleTheme,
    }),
    [effectiveTheme, toggleTheme]
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
